import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { MasterService } from '../../shared/services/master.service';
import { UserService } from '../../shared/services/user.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private router: Router,
    private http: HttpClient,
    private cookie: CookieService,
    @Inject('BASE_URL') private baseUrl: string,
    private masterService: MasterService,
    private userService: UserService,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.userService.getAuthToken();

    if (token) {
      request = this.addToken(request, token);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401Error(request, next);
        }
        return throwError(() => error);
      }),
    );
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.cookie.get('refreshToken');

      if (!refreshToken) {
        this.isRefreshing = false;
        this.userService.logout();
        return throwError(() => new Error('Refresh token missing'));
      }

      const refreshUrl = `${environment.companyApiUrl}/auth/refresh`;

      return this.http.post<any>(refreshUrl, { refreshToken }).pipe(
        switchMap((response: any) => {
          this.isRefreshing = false;

          this.storeAccessToken(response.accessToken);

          // Notify all waiting requests
          this.refreshTokenSubject.next(response.accessToken);

          // Retry original
          return next.handle(this.addToken(req, response.accessToken));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.userService.logout();
          return throwError(() => err);
        }),
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => next.handle(this.addToken(req, token!))),
      );
    }
  }

  private storeAccessToken(token: string) {
    const hostname = window.location.hostname;

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      this.cookie.set('accessToken', token, undefined, '/', undefined, true, 'None');
    } else {
      this.cookie.set(
        'accessToken',
        token,
        undefined,
        '/',
        `.${environment.domainName}`,
        true,
        'None',
      );
    }
  }
}
